import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from 'axios'
import Vue from 'vue'
import apiEndpoints from '@/constants/apiEndpoints'
import router from '../../../../router'

export default function useUsersList() {
  const toast = useToast()
  const refUserListTable = ref(null)

  const tableColumns = [
    { key: 'user_fullname', sortable: true },
    { key: 'user_username', sortable: true },
    { key: 'user_phone', sortable: true },
    { key: 'user_role', sortable: true },
    { key: 'user_branch_code', sortable: true },
    { key: 'actions' },
  ]

  const perPage = ref(10)
  const totalUsers = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('user_fullname')
  const isSortDirDesc = ref(true)
  const roleFilter = ref(null)
  const branchFilter = ref(null)
  const statusFilter = ref(null)

  // Switchable views: 'card' (default) or 'table'.
  const viewMode = ref('card')

  // Card view keeps its own reactive list, since it isn't driven by
  // b-table's items-provider pattern like the table view is.
  const users = ref([])
  const isLoadingUsers = ref(false)
  const loading = ref(false)

  const getLocalItemsCount = () => {
    if (viewMode.value !== 'table') return users.value.length
    return refUserListTable.value ? refUserListTable.value.localItems.length : 0
  }

  const dataMeta = computed(() => {
    const localItemsCount = getLocalItemsCount()
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalUsers.value,
    }
  })

  const getOrderSort = () => {
    if (viewMode.value === 'card') return 'ASC'
    return isSortDirDesc.value ? 'DESC' : 'ASC'
  }

  const buildParams = () => ({
    limit: perPage.value,
    offset: perPage.value * (currentPage.value - 1),
    role: roleFilter.value,
    branch_id: branchFilter.value,
    search: searchQuery.value,
    // Card view groups users A-Z, so it always asks the backend for an
    // alphabetical sort regardless of the table's own sort controls.
    order_by: viewMode.value === 'card' ? 'user_fullname' : sortBy.value,
    order_sort: getOrderSort(),
  })

  const fetchUsersData = () => {
    const userToken = Vue.$cookies.get('userToken')
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userToken}`,
    }

    isLoadingUsers.value = true
    return axios
      .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_USER}`, {
        headers,
        params: buildParams(),
      })
      .then(response => {
        if (response.data && response.data.data !== null) {
          totalUsers.value = response.data.data_count || 0
          users.value = response.data.data
        } else {
          users.value = []
          totalUsers.value = 0
        }
      })
      .catch(e => {
        if (e.message === 'Request failed with status code 401') {
          router.push({ name: 'auth-login' })
        } else {
          console.error(e)
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error fetching data list',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        }
      })
      .finally(() => {
        isLoadingUsers.value = false
      })
  }

  const refetchData = () => {
    if (viewMode.value === 'table') {
      if (refUserListTable.value) refUserListTable.value.refresh()
    } else {
      fetchUsersData()
    }
  }

  watch([currentPage, perPage, searchQuery, sortBy, isSortDirDesc, roleFilter, branchFilter, statusFilter], () => {
    refetchData()
  })

  // Switching view modes should reset to page 1 and reload in the new
  // mode's own way (card mode fetches directly; table mode's freshly
  // mounted b-table calls fetchUsers itself as soon as it renders).
  watch(viewMode, () => {
    currentPage.value = 1
    if (viewMode.value === 'card') {
      fetchUsersData()
    }
  })

  const fetchUsers = (ctx, callback) => {
    const userToken = Vue.$cookies.get('userToken')
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userToken}`,
    }

    loading.value = true
    axios
      .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_USER}`, {
        headers,
        params: buildParams(),
      })
      .then(response => {
        if (response.data && response.data.data !== null) {
          totalUsers.value = response.data.data_count || 0
          callback(response.data.data)
        } else {
          callback([])
        }
      })
      .catch(e => {
        if (e.message === 'Request failed with status code 401') {
          router.push({ name: 'auth-login' })
        } else {
          console.error(e)
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error fetching data list',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        }
      })
      .finally(() => {
        loading.value = false
      })
  }

  const fetchRoles = () => {
    // Fixed 'this' context issue
    const userToken = Vue.$cookies.get('userToken')
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userToken}`,
    }

    return axios
      .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_ROLES}`, { headers })
      .then(response => {
        // Return dynamic roles from API if available, else fallback
        if (response.data && response.data.data) {
          return response.data.data.map(elem => ({
            label: elem.role_name,
            value: elem.role_id,
          }))
        }

        return [
          { label: 'Admin', value: 'admin' },
          { label: 'Author', value: 'author' },
          { label: 'Editor', value: 'editor' },
          { label: 'Maintainer', value: 'maintainer' },
          { label: 'Subscriber', value: 'subscriber' },
        ]
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching role list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return []
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserRoleVariant = role => {
    if (role === 'subscriber') return 'primary'
    if (role === 'author') return 'warning'
    if (role === 'maintainer') return 'success'
    if (role === 'editor') return 'info'
    if (role === 'super-admin') return 'danger'
    return 'primary'
  }

  const resolveUserRoleIcon = role => {
    if (role === 'subscriber') return 'UserIcon'
    if (role === 'author') return 'SettingsIcon'
    if (role === 'maintainer') return 'DatabaseIcon'
    if (role === 'editor') return 'Edit2Icon'
    if (role === 'super-admin') return 'ServerIcon'
    return 'UserIcon'
  }

  const resolveUserStatusVariant = status => {
    if (status === 'pending') return 'warning'
    if (status === 'active') return 'success'
    if (status === 'inactive') return 'secondary'
    return 'primary'
  }

  return {
    fetchUsers,
    fetchUsersData,
    fetchRoles,
    tableColumns,
    perPage,
    currentPage,
    totalUsers,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refUserListTable,
    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveUserStatusVariant,
    refetchData,

    // Switchable views
    viewMode,
    users,
    isLoadingUsers,
    loading,

    // Extra Filters
    roleFilter,
    branchFilter,
    statusFilter,
  }
}
